import { Shield } from 'lucide-react'
import { type ReactNode } from 'react'
import { cn } from '#app/utils/misc.tsx'
import { formatTier, toRoman } from '#app/utils/skill-format.ts'

export function TierMark({
	tier,
	big = false,
	className,
}: {
	tier: number | null | undefined
	big?: boolean
	className?: string
}) {
	if (tier == null) return null
	return (
		<span
			title={formatTier(tier)}
			aria-label={formatTier(tier)}
			className={cn(
				'inline-flex shrink-0 items-center justify-center rounded border border-transparent px-1.5 font-bold tracking-[.07em] text-indigo-700 dark:text-indigo-300',
				big
					? 'h-5 min-w-[26px] text-[11.5px]'
					: 'h-[17px] min-w-[22px] text-[10.5px]',
				tier === 1 && 'opacity-85',
				tier >= 2 && 'bg-indigo-500/10 dark:bg-indigo-400/15',
				tier >= 3 && 'border-indigo-500/30 dark:border-indigo-300/40',
				className,
			)}
		>
			{toRoman(tier)}
		</span>
	)
}

export function CombatMark({
	label = false,
	size = 13,
	className,
}: {
	label?: boolean
	size?: number
	className?: string
}) {
	return (
		<span
			title='Compétence de combat'
			className={cn(
				'text-muted-foreground inline-flex shrink-0 items-center gap-1.5',
				className,
			)}
		>
			<Shield
				style={{ width: size, height: size }}
				strokeWidth={1.7}
				aria-hidden={label ? true : undefined}
				aria-label={label ? undefined : 'Compétence de combat'}
				role={label ? undefined : 'img'}
			/>
			{label ? <span className='text-[11.5px]'>Combat</span> : null}
		</span>
	)
}

export function DepartMark({ dense = false }: { dense?: boolean }) {
	return (
		<span className='inline-flex shrink-0 items-center gap-1 text-emerald-700 dark:text-emerald-300'>
			<span className='size-1 rounded-full bg-current' aria-hidden='true' />
			<span
				className={cn(
					'font-medium tracking-[.02em]',
					dense ? 'text-[10.5px]' : 'text-[11px]',
				)}
			>
				Départ
			</span>
		</span>
	)
}

export type Metric = {
	value: ReactNode
	unit?: string | null
	quiet?: boolean
}

export function Metrics({
	items,
	size = 12,
	muted = false,
	className,
}: {
	items: Array<Metric | false | null | undefined>
	size?: number
	muted?: boolean
	className?: string
}) {
	const shown = items.filter((item): item is Metric => Boolean(item))
	if (shown.length === 0) return null
	const label = shown
		.map((item) => (item.unit ? `${item.value} ${item.unit}` : `${item.value}`))
		.join(' · ')
	return (
		<span
			aria-label={label}
			className={cn(
				'inline-flex items-baseline gap-[7px] whitespace-nowrap tabular-nums',
				className,
			)}
			style={{ fontSize: size }}
		>
			{shown.map((item, index) => (
				<span key={index} className='contents'>
					{index > 0 ? (
						<span aria-hidden='true' className='text-border'>
							·
						</span>
					) : null}
					<span
						className={cn(
							'font-medium',
							item.quiet || muted ? 'text-muted-foreground' : 'text-foreground',
						)}
					>
						{item.value}
						{item.unit ? (
							<span
								className='text-muted-foreground ml-[2.5px] font-normal'
								style={{ fontSize: size - 1.5 }}
							>
								{item.unit}
							</span>
						) : null}
					</span>
				</span>
			))}
		</span>
	)
}

export function Eyebrow({
	children,
	className,
}: {
	children: ReactNode
	className?: string
}) {
	return (
		<span
			className={cn(
				'text-muted-foreground block text-[10px] font-semibold tracking-[.14em] uppercase',
				className,
			)}
		>
			{children}
		</span>
	)
}

export function SkillTeaser({
	text,
	lines = 2,
	className,
}: {
	text: string | null | undefined
	lines?: 2 | 3
	className?: string
}) {
	if (!text) return null
	return (
		<p
			className={cn(
				'font-book text-muted-foreground text-[13.5px] leading-normal',
				lines === 3 ? 'line-clamp-3' : 'line-clamp-2',
				className,
			)}
		>
			{text}
		</p>
	)
}

export function TierDivider({ tier }: { tier: number }) {
	return (
		<div className='flex items-center gap-3'>
			<div className='bg-border h-px flex-1' />
			<Eyebrow>{formatTier(tier)}</Eyebrow>
			<div className='bg-border h-px flex-1' />
		</div>
	)
}
